import { Badge, BadgeVariant, BadgeSize } from "shared/components";
import { ApplicantRoleLabel } from "shared/utils";
import {
  AgentEscalationDetectedActivity,
  AgentActionTakenActivity,
  AgentEscalatedToSupportActivity,
  AgentEscalationReviewedActivity
} from "../types";
import { ApplicantType } from "shared/types";

export type AgentActivity =
  | AgentEscalationDetectedActivity
  | AgentActionTakenActivity
  | AgentEscalatedToSupportActivity
  | AgentEscalationReviewedActivity;

const CONFIG = {
  agent_escalation_detected: {
    title: "Escalation signal detected"
  },
  agent_action_taken: {
    title: "Action taken on escalation signal",
    action: {
      badgeLabel: "Action",
      badgeVariant: BadgeVariant.PRIMARY,
      badgeClassName: "!bg-primary-500 !text-white",
      dotClassName: "bg-white",
      containerClassName: "bg-primary-20",
      textClassName: "text-primary-600"
    }
  },
  agent_escalated_to_support: {
    title: "Action taken on escalation signal",
    action: {
      badgeLabel: "Escalated to support",
      badgeVariant: BadgeVariant.PRIMARY,
      badgeClassName: "!bg-primary-500 !text-white",
      dotClassName: "bg-white",
      containerClassName: "bg-primary-20",
      textClassName: "text-primary-600"
    }
  },
  agent_escalation_reviewed: {
    title: "Escalation signal reviewed",
    action: {
      badgeLabel: "No action taken",
      badgeVariant: BadgeVariant.WARNING,
      badgeClassName: "!bg-warning-600 !text-white",
      dotClassName: "bg-white",
      containerClassName: "bg-warning-50",
      textClassName: "text-warning-700"
    }
  }
} as const;

const getActionText = (item: AgentActivity): string => {
  switch (item.activity_type) {
    case "agent_action_taken":
    case "agent_escalated_to_support":
      return item.details.action_description;
    case "agent_escalation_reviewed":
      return item.details.reason;
    default:
      return "";
  }
};

const getApplicantRoleLabel = (role?: ApplicantType): string => {
  return ApplicantRoleLabel[role || ApplicantType.Applicant];
};

interface AgentActivityCardProps {
  activityItem: AgentActivity;
}

export const AgentActivityCard = ({ activityItem }: AgentActivityCardProps) => {
  if (typeof activityItem.details !== "object") return null;

  const config = CONFIG[activityItem.activity_type];
  const { details, author } = activityItem;

  return (
    <div className="space-y-3">
      <p className="text-base font-bold text-content-1000">{config.title}</p>

      {details.original_message ? (
        <div className="rounded-lg bg-background-900 p-4 space-y-2">
          <Badge
            variant={BadgeVariant.BASE}
            size={BadgeSize.SM}
            className="items-center !text-xs !leading-4 !font-bold"
            leftElement={
              <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-content-300" />
            }
          >
            Message
          </Badge>
          <p className="text-sm font-medium text-content-700">
            {details.original_message}
          </p>
        </div>
      ) : null}

      {"action" in config ? (
        <div
          className={`rounded-lg ${config.action.containerClassName} p-4 space-y-2`}
        >
          <Badge
            variant={config.action.badgeVariant}
            size={BadgeSize.SM}
            className={`items-center ${config.action.badgeClassName} !text-xs !leading-4 !font-bold`}
            leftElement={
              <span
                className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${config.action.dotClassName}`}
              />
            }
          >
            {config.action.badgeLabel}
          </Badge>
          <p className={`text-sm font-medium ${config.action.textClassName}`}>
            {getActionText(activityItem)}
          </p>
        </div>
      ) : null}

      <div className="flex items-center justify-between text-sm text-content-400">
        {details.applicant_name ? (
          <span>
            {getApplicantRoleLabel(details.applicant_role)}:{" "}
            <span className="font-medium">{details.applicant_name}</span>
          </span>
        ) : null}
        {author?.name ? (
          <span>
            Agent: <span className="font-medium">{author.name}</span>
          </span>
        ) : null}
      </div>
    </div>
  );
};
